// ============================================================
// TMR Portal Admin — the contract, as the backend actually sends it
// ============================================================

/**
 * Where the contract is in its lifecycle (02 §5, `ContractStatus`).
 *
 * Only `Pending` is actionable from the portal: it is the one state `RequestSignature` accepts, and every
 * other value is either already at the provider or closed. See `canRequestSignature` in `./status`.
 *
 * The four closing states are **reflections, not decisions** — the document's deadline, its expiry and its
 * reminders belong to the signature provider (02 §11), so `Expired`/`Refused`/`Cancelled` are only ever
 * written when the provider says so. Nothing in the portal or the backend expires a contract on its own.
 */
export type ContractStatus =
  | 'Pending'
  | 'AwaitingSignature'
  | 'Signed'
  | 'Cancelled'
  | 'Expired'
  | 'Refused';

/**
 * Which kind of party a contract binds (02 §5).
 *
 * `Patient` exists in the type because the backend's enum carries it, but creating one is **refused with a
 * 400** while the Patients module does not exist (02 §11) — so the portal never offers it as a choice. It is
 * here to describe what a response may contain, not what a request may ask for.
 */
export type ContractPartyType = 'Company' | 'Patient';

/**
 * How the signature request reached — or would reach — the party's signatory (02 §7).
 *
 * A union rather than a plain string, unlike `signatureProvider`: these two are the whole vocabulary and
 * the screen branches on them, so a third value arriving should be a type error to fix rather than a label
 * that silently falls through.
 */
export type SignatureDeliveryChannel = 'WhatsApp' | 'Email';

/**
 * One contract, in the single shape every contract endpoint answers with — the list, the detail and the
 * creation response are all this (`ContractResponse`).
 *
 * What it deliberately does **not** carry: the document itself, its clauses, its page count, the list of
 * signatories, or any signature history. The document lives in a template hosted at the provider (03 §6)
 * and tmr_v2 stores none of its content — so a screen showing "the contract" can only show what is here
 * plus a link out to the provider. There is no endpoint to download the signed PDF (09, lacuna L4).
 *
 * It also carries no validity dates. A contract has no `start`/`end` in tmr_v2 because the deadline is the
 * provider's (02 §11), which is why nothing here can answer "which contract is the current one".
 */
export interface ContractRecord {
  id: string;
  partyType: ContractPartyType;
  /** The party's id in tmr_v2 — a Company id today. The contract holds it loosely and never resolves it. */
  partyId: string;
  /**
   * The party's id in the legacy system (`produtores.id`); null when the Empresa has not been exported.
   *
   * A coexistence artifact (03 §12), and the one the repasse bridge needs to find its legacy row — so a
   * contract created before its Empresa was exported can never release the repasse, and nothing in the
   * portal can fix it afterwards (09, lacuna L7).
   */
  legacyPartyId: number | null;
  /**
   * Which provider holds the document. A plain string, not a union: v1 only ever answers `ClickSign`, but
   * three more are designed (02 §7), and a union would break against a value the API starts sending. The
   * label falls back to the raw key instead — see `signatureProviderLabel` in `./status`.
   */
  signatureProvider: string;
  status: ContractStatus;
  /** The document's key at the provider; null until it has been sent. This is what the webhook matches on. */
  signatureExternalId: string | null;
  /**
   * The document's full path at the provider — folder and file name — as it was when we raised it. Null
   * until the contract is sent. Raw on purpose: how it reads on screen is the screen's business, which is
   * what `documentDisplayName` in `./status` is for.
   */
  documentPath: string | null;
  /**
   * Which channel carried the request to the signatory. Null until the contract is sent — and **also null
   * on contracts raised before the choice existed**, which is not the same as "por e-mail": nobody
   * recorded it, so nothing here may claim they did.
   */
  signatureDeliveryChannel: SignatureDeliveryChannel | null;
  /**
   * The contact the provider was given, as it stood when the document was raised. It answers the question
   * the channel alone cannot — *which* number — and it does not follow later edits to the cadastre, which
   * is exactly why it is stored rather than resolved.
   */
  signatureDeliveryTarget: string | null;
  /** When the document was created at the provider — ISO-8601, like every date in this layer. */
  signatureRequestedAt: string | null;
  /** When the Empresa's legal representative signed. */
  partySignedAt: string | null;
  /** When the TMR counter-signed. Independent of the above: either side may sign first (02 §11). */
  tmrSignedAt: string | null;
  /**
   * When the contract was discarded from the portal.
   *
   * Null while it runs and **also null when the provider cancelled it** — a `Cancelled` status with no
   * stamp here means the decision was taken at the provider, which the status alone cannot say.
   */
  cancelledAt: string | null;
  /** Why it was discarded, when the operator said. */
  cancellationReason: string | null;
  createdAt: string;
}

/**
 * What `POST /contracts` takes. Build it with {@link toCreateContractRequest} rather than by hand — there
 * is more than one way to fill it in wrongly.
 */
export interface CreateContractRequest {
  partyType: ContractPartyType;
  partyId: string;
  legacyPartyId: number | null;
}

/**
 * What `POST /contracts/{id}/signature` takes.
 *
 * The whole body is optional and so is the field: omitting it means "use the channel the preview would
 * have defaulted to", which the server decides. The portal always sends one anyway — it has just shown the
 * operator a selector, so leaving the choice implicit would be discarding the answer it just collected.
 */
export interface RequestSignatureRequest {
  channel: SignatureDeliveryChannel;
}

/**
 * What `POST /contracts/{id}/signature` answers with.
 *
 * The `signatureUrl` is not stored: the provider owns it and may invalidate it, so it is never cached. It
 * can be read again, live, from `GET /contracts/{id}/signing-link` while the contract awaits signature.
 */
export interface RequestSignatureResponse {
  contract: ContractRecord;
  signatureUrl: string | null;
}

/**
 * What `POST /contracts/{id}/signatory` takes — where a contract already out for signature should go
 * instead (02 §7).
 *
 * A one-off for this send: the Empresa's cadastre is left alone. A contact left out (or blank) means the
 * one in the cadastre, which is how a document is re-pointed after the party itself was corrected.
 */
export interface ChangeSignatoryRequest {
  /** Required — this call exists to change where the request lands, so the server never guesses it. */
  channel: SignatureDeliveryChannel;
  phone?: string;
  email?: string;
}

/** What `POST /contracts/{id}/signatory` answers with: the re-pointed contract and its new signing link. */
export interface ChangeSignatoryResponse {
  contract: ContractRecord;
  signatureUrl: string | null;
}

/**
 * Where the Empresa's representative signs (`GET /contracts/{id}/signing-link`).
 *
 * Read from the provider on every call and never cached: replacing the signatory invalidates it.
 */
export interface ContractSigningLink {
  url: string;
  /**
   * How the representative authenticates on opening it. For `WhatsApp` the access token goes to their
   * phone, so the link handed to anyone else signs nothing — the screen has to say so.
   */
  channel: SignatureDeliveryChannel;
}

/**
 * The document as the provider currently describes it (`GET /contracts/{id}/document`).
 *
 * **Never cache this.** It describes a moment at the provider, not a fact about the contract — the status
 * and the event list change under it. The backend already answers `no-store`.
 *
 * It carries **no download URLs**, deliberately. The provider's links expire within minutes and, for a
 * `.docx`, a browser navigating to one hands it to the Office online viewer, which fails to fetch it and
 * shows its own 404. The files are downloaded from our own API instead — see `contractFileUrl`.
 */
export interface ContractDocument {
  /** The document's full path at the provider. */
  path: string | null;
  /**
   * The provider's own status word (`running`, `closed`, `canceled`), not our `ContractStatus`. The two can
   * legitimately disagree while a callback is in flight, and seeing that is often why someone opened this.
   */
  status: string | null;
  deadlineAt: string | null;
  /** Whether a signed file exists yet — it only does once the document closes. */
  hasSignedFile: boolean;
  /** Whether the document as raised can be downloaded. */
  hasOriginalFile: boolean;
  /**
   * Whether reading the provider moved the contract — a signature it had recorded and we had not.
   *
   * The contract's own fields come from a different request, so without this signal the panel would go on
   * showing "ainda não" beside the timeline that just corrected it. When true, refetch the contract.
   */
  reconciled: boolean;
  /** Everything the provider recorded, oldest first — including what our webhook ignores. */
  events: ContractDocumentEvent[];
}

/** Which side of the contract a signature came from, when the provider attributes it. */
export type ContractSignerRole = 'Party' | 'Tmr';

/** One thing that happened to the document, as the provider recorded it. */
export interface ContractDocumentEvent {
  /** The provider's raw event name — `upload`, `add_signer`, `view`, `sign`, `auto_close`… */
  name: string;
  occurredAt: string | null;
  actorName: string | null;
  actorEmail: string | null;
  /**
   * Who signed, for a signature event; null otherwise. Resolved by the backend — telling the TMR's
   * counter-signature from the Empresa's needs the TMR signatory address, which is server configuration.
   */
  actorRole: ContractSignerRole | null;
}

/**
 * What sending a contract would produce (`GET /contracts/{id}/preview`) — the check before the click.
 *
 * Everything here is rebuilt by the backend on each read from the very code that fills the provider's
 * template, so it shows what the document will print rather than a second rendering of the same data. Do
 * **not** reassemble any of it in the browser: the contract's masks and layout are not the portal's
 * (`companyAddress` uses different separators), so a locally built preview would describe a document nobody
 * is going to sign.
 */
export interface ContractPreview {
  /**
   * Whether the contract can be sent **through `defaultChannel`** as it stands. Prefer the selected
   * channel's own `ready` when the screen lets the operator switch: this one only answers for the default.
   */
  ready: boolean;
  /**
   * Everything worth seeing first that holds *whatever channel is chosen* — blocking or not. What depends
   * on the channel lives in that channel's own `issues`, so the screen never has to work out which of two
   * lists a message came from.
   */
  issues: ContractPreviewIssue[];
  /**
   * Every way the request could reach the signatory, best first. **Empty when the Empresa could not be
   * resolved** — there is no channel question to ask about a document that cannot be built, so the screen
   * must not index into this blindly.
   */
  channels: ContractPreviewChannel[];
  /**
   * The channel to open on: the best one that actually works right now, or null when none does. Comes from
   * the server because it depends on facts the browser does not have — whether the environment has
   * WhatsApp enabled at the provider, and whether the stored phone is one a provider can dial.
   */
  defaultChannel: SignatureDeliveryChannel | null;
  /** The Empresa this contract is for — where "corrigir o cadastro" has to go. */
  companyId: string;
  companyName: string | null;
  /** Its id in the legacy system; null while the replication has not finished. */
  legacyProducerId: number | null;
  /** What the document would be called at the provider; null when the preview could not be built. */
  documentName: string | null;
  deadlineAt: string | null;
  /** Every value the contract would print, in the order the document reads them. */
  fields: ContractPreviewField[];
  signatories: ContractPreviewSignatory[];
}

/** One way the signature request could reach the party's signatory. */
export interface ContractPreviewChannel {
  channel: SignatureDeliveryChannel;
  /** Whether it can be used at all — covers both "this environment lacks it" and "this signatory cannot be reached". */
  available: boolean;
  /** The contact it would go to, already formatted; null when there is none — usually why it is unavailable. */
  target: string | null;
  /**
   * Whether the contract can be sent through this channel. **Bind the confirm button to this**, not to the
   * top-level `ready`: it already folds in the document's own blocking issues, so intersecting two lists in
   * the browser would only be a chance to get the intersection subtly wrong.
   */
  ready: boolean;
  /** What is wrong with this channel specifically, so the reason can sit beside the option it disables. */
  issues: ContractPreviewIssue[];
}

/** Something to see before the document goes out. */
export interface ContractPreviewIssue {
  /** A stable key, so the screen acts on it without matching on prose. */
  code: string;
  message: string;
  /** Whether sending would fail. False is the interesting case: it goes out with the problem printed in it. */
  blocking: boolean;
}

/** One value the contract would print, already masked and laid out as the document shows it. */
export interface ContractPreviewField {
  /** The template's placeholder key, so a mismatch with the hosted model is visible. */
  key: string;
  /** What the value is called in the contract — the backend's, not the screen's (only it knows the model). */
  label: string;
  /** Null when the placeholder would print blank. */
  value: string | null;
}

/** Someone the document would be put in front of. */
export interface ContractPreviewSignatory {
  /** Which side of the contract they are. */
  role: ContractSignerRole;
  name: string;
  /** Null when the cadastre has none — which a WhatsApp send allows, and an e-mail send refuses. */
  email: string | null;
  documentation: string | null;
  /** Whether they would be signed for programmatically rather than by e-mail — the TMR's own path. */
  signsThroughApi: boolean;
  /** Their phone, already formatted; null when there is none the provider could use. */
  phone: string | null;
}

/** What `POST /contracts/{id}/cancel` takes. The reason is optional and free text. */
export interface CancelContractRequest {
  reason: string | null;
}

/** The bits of an Empresa a contract is drawn up from. Structural, so both the list item and the full record fit. */
export interface ContractPartySource {
  /** The Empresa's id in tmr_v2. */
  id: string;
  /** Its id in the legacy system; null until the export succeeds. */
  legacyProducerId: number | null;
}

/**
 * Builds the creation payload for an Empresa.
 *
 * It exists because each of the three fields has a plausible wrong value:
 *
 * - `partyType` is always `Company` — never a form field. `Patient` is refused with a 400 (02 §11), so
 *   offering the choice would be offering a guaranteed error.
 * - `partyId` is the **tmr_v2 Guid**, never `legacyProducerId`. Swapping them draws up a contract for a
 *   party that does not exist, or trips the operator's scope and reads back as "not found".
 * - `legacyPartyId` is `null` — never `0`, never `undefined` — for an Empresa that has not been exported.
 *   The handler rejects a non-positive id, so a `0` here answers "o id legado da parte precisa ser
 *   positivo" to somebody who never typed an id.
 *
 * @param company The Empresa the contract is for.
 */
export function toCreateContractRequest(company: ContractPartySource): CreateContractRequest {
  return {
    partyType: 'Company',
    partyId: company.id,
    legacyPartyId: company.legacyProducerId ?? null,
  };
}
